import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  Warning as DegradedIcon,
  Error as UnhealthyIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { statusApi } from '../services/api';
import { SystemStatus, ServiceHealth } from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { useApiQuery } from '../hooks/useApiQuery';
import ErrorMessage from '../components/ErrorMessage';

export default function StatusPage() {
  const { data: status, loading, error, refetch } = useApiQuery<SystemStatus>(
    () => statusApi.getStatus()
  );

  const getStatusIcon = (health: ServiceHealth) => {
    switch (health.status) {
      case 'healthy':
        return <HealthyIcon color="success" fontSize="large" />;
      case 'degraded':
        return <DegradedIcon color="warning" fontSize="large" />;
      case 'unhealthy':
        return <UnhealthyIcon color="error" fontSize="large" />;
      default:
        return null;
    }
  };

  const getStatusColor = (health: ServiceHealth): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (health.status) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (health: ServiceHealth) => {
    switch (health.status) {
      case 'healthy':
        return 'Gesund';
      case 'degraded':
        return 'Eingeschränkt';
      case 'unhealthy':
        return 'Nicht verfügbar';
      default:
        return 'Unbekannt';
    }
  };

  if (loading && !status) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">System Status</Typography>
        <Tooltip title="Systemstatus erneut prüfen">
          <Button
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Tooltip>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Systemstatus konnte nicht geladen werden"
          onRetry={refetch}
        />
      )}

      {status && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Letzter Check:{' '}
            {formatDateSafe(status.timestamp, 'dd.MM.yyyy HH:mm:ss')}
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {getStatusIcon(status.services.cosmosdb)}
                    <Box flex={1}>
                      <Typography variant="h6">CosmosDB</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Datenbank für Schulungsläufe und Logs
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusLabel(status.services.cosmosdb)}
                      color={getStatusColor(status.services.cosmosdb)}
                    />
                  </Box>
                  {status.services.cosmosdb.message && (
                    <Alert
                      severity={
                        status.services.cosmosdb.status === 'healthy'
                          ? 'success'
                          : status.services.cosmosdb.status === 'degraded'
                          ? 'warning'
                          : 'error'
                      }
                    >
                      {status.services.cosmosdb.message}
                    </Alert>
                  )}
                  {status.services.cosmosdb.responseTime && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Antwortzeit: {status.services.cosmosdb.responseTime}ms
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {getStatusIcon(status.services.blobStorage)}
                    <Box flex={1}>
                      <Typography variant="h6">Blob Storage</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Speicher für Logs und Schulungsmaterialien
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusLabel(status.services.blobStorage)}
                      color={getStatusColor(status.services.blobStorage)}
                    />
                  </Box>
                  {status.services.blobStorage.message && (
                    <Alert
                      severity={
                        status.services.blobStorage.status === 'healthy'
                          ? 'success'
                          : status.services.blobStorage.status === 'degraded'
                          ? 'warning'
                          : 'error'
                      }
                    >
                      {status.services.blobStorage.message}
                    </Alert>
                  )}
                  {status.services.blobStorage.responseTime && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Antwortzeit: {status.services.blobStorage.responseTime}ms
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Gesamtstatus
              </Typography>
              {status.services.cosmosdb.status === 'healthy' &&
              status.services.blobStorage.status === 'healthy' ? (
                <Alert severity="success">Alle Services sind verfügbar</Alert>
              ) : status.services.cosmosdb.status === 'unhealthy' ||
                status.services.blobStorage.status === 'unhealthy' ? (
                <Alert severity="error">
                  Kritische Services sind nicht verfügbar. Bitte Administrator kontaktieren.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Einige Services laufen mit Einschränkungen
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
